// Launcher-side character selection — what boot renders, and the URL that records it.
//
// The rule is one line: **a ?character= deep link loads that character; anything
// else shows the launcher.** Opening the app is therefore always an explicit
// choice, never a resume you have to notice and undo.
//
// Why this module exists separately from characterState.js: resolveId() there
// ends in `list[0]?.id`, so there has never been a state where no character is
// selected — boot always auto-loaded something. That fallback is load-bearing
// (loadBlueprint.js derives per-character storage keys from resolveCharacterId()
// before any fetch resolves, and getCurrentCharacter() must return a record), so
// it is NOT changed here. The launcher decision is made BEFORE resolution is
// reached, so every existing consumer of resolveId / resolveCharacterId /
// resolveActiveCharacter behaves identically.
//
// The scoped localStorage id keeps its other jobs — deriving storage keys, and
// ordering the launcher's recents — it just no longer decides what boot shows.

// Extension is explicit: this module is covered by `node --test`, which does not
// resolve extensionless specifiers the way Vite does.
import { scopeKey } from './userScope.js'

const LS_KEY = 'aos-character-v1'
const RECENTS_KEY = 'aos-character-recents-v1'
const PARAM = 'character'

// How many characters the launcher's RECENT row holds. Three fits one row at
// the launcher's card size; the rest are reachable from the full grid below it.
export const RECENTS_LIMIT = 6

function defaultWin() {
  return typeof window !== 'undefined' ? window : undefined
}

function readParam(win) {
  try {
    return new URL(win.location.href).searchParams.get(PARAM)
  } catch {
    return null
  }
}

/**
 * The character named by a ?character= deep link, when it exists on the server.
 * `null` means "show the launcher".
 *
 * A deep link is the ONLY thing that skips the launcher. The stored selection is
 * deliberately NOT consulted: boot always lands on the picker, so opening the app
 * is an explicit choice rather than a resume you have to notice and undo. The
 * stored id stays meaningful — resolveCharacterId() still derives per-character
 * storage keys from it, and the launcher orders its recents by it — it just no
 * longer decides what boot renders.
 *
 * A param naming a character absent from the list returns null too, so a dead
 * link lands on the picker instead of silently loading a different character.
 * Callers that want to TELL the user their link is dead should compare against
 * readCharacterParam() first.
 *
 * @param {Array<{id: string}>} list  the server character list
 * @param {Window} [win]
 * @returns {string|null}
 */
export function resolveDeepLinkedCharacterId(list, win = defaultWin()) {
  if (!Array.isArray(list) || list.length === 0) return null
  const id = readCharacterParam(win)
  if (!id) return null
  return list.some(c => c.id === id) ? id : null
}

/** The raw ?character= value, whether or not it names a real character. */
export function readCharacterParam(win = defaultWin()) {
  if (!win?.location?.href) return null
  return readParam(win)
}

/**
 * Remove ?character= from the URL, pushing a history entry — the inverse of
 * pinCharacterUrlParam, used when the user asks for the launcher from the menu.
 *
 * It does not touch the stored selection, which no longer decides what boot shows
 * anyway — a param-less URL is the launcher, so stripping the param is the whole
 * job. Reloading here lands on the launcher, matching the address bar.
 *
 * @returns {boolean} true when a history entry was pushed.
 */
export function unpinCharacterUrlParam(win = defaultWin()) {
  if (!win?.location?.href || typeof win.history?.pushState !== 'function') return false

  let url
  try { url = new URL(win.location.href) } catch { return false }
  if (!url.searchParams.has(PARAM)) return false

  url.searchParams.delete(PARAM)
  try {
    win.history.pushState(win.history.state ?? null, '', url.toString())
  } catch {
    return false
  }
  return true
}

/**
 * ADD (or change) ?character=<id> on the URL, pushing a history entry.
 *
 * Distinct from reconcileCharacterUrlParam, which deliberately only rewrites a
 * param that is ALREADY present — it must not make the address bar sticky after
 * an in-place switch the user never opted into. Picking from the launcher is the
 * opposite case: it IS an explicit navigation, so the URL should record it
 * (reload stays put, the link is shareable) and Back should return to the
 * launcher. Hence pushState here versus replaceState there.
 *
 * @returns {boolean} true when a history entry was pushed.
 */
export function pinCharacterUrlParam(id, win = defaultWin()) {
  if (!id) return false
  if (!win?.location?.href || typeof win.history?.pushState !== 'function') return false

  let url
  try { url = new URL(win.location.href) } catch { return false }
  if (url.searchParams.get(PARAM) === id) return false

  url.searchParams.set(PARAM, id)
  try {
    win.history.pushState(win.history.state ?? null, '', url.toString())
  } catch {
    return false
  }
  return true
}

/**
 * Most-recently-opened characters, newest first: `[{ id, at }]` where `at` is
 * epoch ms, or `null` when unknown.
 *
 * MIGRATION: this key used to hold a bare `string[]` of ids. Those entries are
 * read as `{ id, at: null }` — a known-unknown, so the launcher renders no
 * "opened …" line for them rather than inventing one. They gain a real timestamp
 * the next time the character is opened.
 *
 * Never throws: a corrupt value reads as empty, which costs the recents row and
 * nothing else.
 */
export function readRecentCharacters(win = defaultWin(), lsKey = scopeKey(RECENTS_KEY)) {
  if (!win) return []
  let parsed
  try {
    const raw = win.localStorage?.getItem(lsKey)
    if (!raw) return []
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []

  const out = []
  for (const entry of parsed) {
    if (typeof entry === 'string') {
      out.push({ id: entry, at: null })          // legacy shape
    } else if (entry && typeof entry.id === 'string') {
      out.push({ id: entry.id, at: Number.isFinite(entry.at) ? entry.at : null })
    }
  }
  return out
}

/** Just the ids, newest first — for callers that only need the order. */
export function readRecentCharacterIds(win = defaultWin(), lsKey = scopeKey(RECENTS_KEY)) {
  return readRecentCharacters(win, lsKey).map(e => e.id)
}

/**
 * Record `id` as most-recent, stamped `at`. Idempotent; caps at RECENTS_LIMIT.
 *
 * `at` is a parameter rather than an internal Date.now() so the write is testable
 * and so a caller can replay a known time.
 */
export function pushRecentCharacterId(id, at, win = defaultWin(), lsKey = scopeKey(RECENTS_KEY)) {
  if (!id || !win) return
  const stamped = { id, at: Number.isFinite(at) ? at : null }
  const rest = readRecentCharacters(win, lsKey).filter(e => e.id !== id)
  const next = [stamped, ...rest].slice(0, RECENTS_LIMIT)
  try {
    win.localStorage?.setItem(lsKey, JSON.stringify(next))
  } catch {
    /* storage full / disabled — recents are a convenience, not state of record */
  }
}
